<template>
  <main class="figma-home" :style="stageVars">
    <div class="stage-shell">
      <div class="stage stage--top" :style="topStageStyle" aria-label="StoReel 2026 homepage">
        <FigmaSliceLayer :sections="topFigmaSections" :scaled-px="scaledPx" />
        <HeaderNavOverlay
          :items="headerNavItems"
          :actions="headerActionItems"
          :scaled-px="scaledPx"
        />
        <MaskOverlay :masks="topInteractionMasks" :scaled-px="scaledPx" />
        <IntroStorySection :content="introStorySection" :scaled-px="scaledPx" />
        <HeroCarousel :slides="heroSlides" />
        <FeatureCarousel :slides="featureSlides" />
        <WorkflowVideo :box="workflowVideoBox" :src="WORKFLOW_VIDEO_SRC" :scaled-px="scaledPx" />
        <SectionTitleOverlay
          :box="aboutTitleBox"
          :title="aboutTitle"
          :scaled-px="scaledPx"
          variant="about"
        />
        <AboutStoryCards
          :box="aboutCardsBox"
          :cards="aboutCards"
          :scaled-px="scaledPx"
        />
        <SectionTitleOverlay
          :box="creatorTitleBox"
          :title="creatorTitle"
          :scaled-px="scaledPx"
          variant="creator"
        />
        <ExpandableCards
          label="Creator growth cards"
          :box="creatorCardsBox"
          :cards="creatorCards"
          :scaled-px="scaledPx"
          variant="creator"
        />
        <WorkflowStepsOverlay
          :intro="workflowIntro"
          :cards-slice="workflowCardsSlice"
          :scaled-px="scaledPx"
        />
        <HotspotLayer :hotspots="hotspots" :scaled-px="scaledPx" :stage-scale="stageScale" />
      </div>

      <CanvasCommunity
        :content="canvasCommunitySection"
        :feature-cards="canvasFeatureCards"
        :value-cards="canvasValueCards"
        :scaled-px="scaledPx"
      />

      <div class="support-faq-flow">
        <SupportSystem
          :box="supportSystemBox"
          :cards="supportSystemCards"
          :scaled-px="scaledPx"
          flow
        />
        <CommunityCopy :content="communityCopy" :press-cards="pressCards" :scaled-px="scaledPx" />
        <FaqAccordion
          :box="faqBox"
          :intro="faqIntro"
          :items="faqItems"
          :scaled-px="scaledPx"
          flow
        />
        <ContactFooter :scaled-px="scaledPx" />
      </div>

    </div>
  </main>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { useStageScale } from '../composables/useStageScale'
import {
  WORKFLOW_VIDEO_SRC,
  aboutCards,
  aboutCardsBox,
  aboutTitle,
  aboutTitleBox,
  canvasCommunitySection,
  canvasFeatureCards,
  canvasValueCards,
  communityCopy,
  creatorCards,
  creatorCardsBox,
  creatorTitle,
  creatorTitleBox,
  faqBox,
  faqIntro,
  faqItems,
  featureSlides,
  figmaSections,
  headerActionItems,
  headerNavItems,
  heroSlides,
  hotspots,
  introStorySection,
  interactionMasks,
  pressCards,
  supportSystemBox,
  supportSystemCards,
  workflowCardsSlice,
  workflowIntro,
  workflowVideoBox,
} from '../data/home2026'
import AboutStoryCards from './home2026/AboutStoryCards.vue'
import CanvasCommunity from './home2026/CanvasCommunity.vue'
import ContactFooter from './home2026/ContactFooter.vue'
import ExpandableCards from './home2026/ExpandableCards.vue'
import CommunityCopy from './home2026/CommunityCopy.vue'
import FaqAccordion from './home2026/FaqAccordion.vue'
import FeatureCarousel from './home2026/FeatureCarousel.vue'
import FigmaSliceLayer from './home2026/FigmaSliceLayer.vue'
import HeaderNavOverlay from './home2026/HeaderNavOverlay.vue'
import HeroCarousel from './home2026/HeroCarousel.vue'
import HotspotLayer from './home2026/HotspotLayer.vue'
import IntroStorySection from './home2026/IntroStorySection.vue'
import MaskOverlay from './home2026/MaskOverlay.vue'
import SectionTitleOverlay from './home2026/SectionTitleOverlay.vue'
import SupportSystem from './home2026/SupportSystem.vue'
import WorkflowStepsOverlay from './home2026/WorkflowStepsOverlay.vue'
import WorkflowVideo from './home2026/WorkflowVideo.vue'

const { stageScale, stageVars, scaledPx } = useStageScale()

const topStageEndY = workflowVideoBox.y + workflowVideoBox.h
const introStoryEndY = introStorySection.box.y + introStorySection.box.h
const introVideoSection = figmaSections.find((section) => section.id === '02-intro-video')
const introVideoTailSection = introVideoSection
  ? {
      ...introVideoSection,
      id: '02-intro-video-tail',
      y: introStoryEndY,
      h: introVideoSection.y + introVideoSection.h - introStoryEndY,
      sourceH: introVideoSection.h,
      cropTop: introStoryEndY - introVideoSection.y,
    }
  : null

const topFigmaSections = computed(() =>
  [
    ...figmaSections.filter(
      (section) => section.id !== '02-intro-video' && section.y < topStageEndY,
    ),
    ...(introVideoTailSection ? [introVideoTailSection] : []),
  ].sort((a, b) => a.y - b.y),
)

const topInteractionMasks = computed(() =>
  interactionMasks.filter((mask) => mask.y < topStageEndY),
)

const topStageStyle = computed(() => ({
  height: scaledPx(topStageEndY),
}))
</script>

<style scoped>
:global(html),
:global(body),
:global(#app) {
  width: 100%;
  min-width: 0;
  height: auto;
  min-height: 100%;
  margin: 0;
  overflow-x: hidden;
  background: #000;
}

:global(body) {
  min-height: 100vh;
}

:global(#app) {
  overflow-y: visible;
}

.figma-home {
  --stage-width: 1920px;
  --stage-height: 21460px;
  --hero-top: 150px;
  --hero-height: 746px;
  --hero-card-y: 20px;
  --hero-dot-y: 702px;
  --hero-dot-gap: 14px;
  --hero-dot-width: 36px;
  --hero-dot-active-width: 42px;
  --hero-dot-height: 12px;
  --hero-perspective: 1400px;
  --radius-30: 30px;
  --shadow-y: 34px;
  --shadow-blur: 36px;
  --feature-top: 3399px;
  --feature-height: 930px;
  --feature-fade-width: 260px;
  --feature-gap: 11px;
  --feature-card-width: 430px;
  --feature-start: -185px;
  --feature-end: -2390px;
  --focus-outline-width: 3px;
  --focus-outline-offset: 4px;
  --video-radius: 8px;
  --expand-card-radius: 30px;
  --expand-card-shadow-y: 24px;
  --expand-card-shadow-blur: 30px;
  --about-expanded-width: 900px;
  --about-collapsed-width: 300px;
  --about-gap: 10px;
  --about-title-font: 50px;
  --about-title-line: 70px;
  --about-padding-x: 50px;
  --about-padding-top: 50px;
  --about-mark-font: 24px;
  --about-copy-top: 47px;
  --about-copy-font: 35px;
  --about-copy-line: 49px;
  --about-collapsed-mark-font: 34px;
  --creator-expanded-width: 1000px;
  --creator-expanded-height: 750px;
  --creator-expanded-right-x: 520px;
  --creator-small-x: 1053px;
  --creator-small-second-top: 400px;
  --creator-small-width: 467px;
  --creator-small-height: 350px;
  --creator-padding-x: 100px;
  --creator-padding-top: 100px;
  --creator-title-font: 50px;
  --creator-title-line: 70px;
  --creator-copy-top: 250px;
  --creator-copy-font: 40px;
  --creator-copy-line: 56px;
  --story-copy-gap: 58px;
  --story-copy-font: 28px;
  --story-copy-line: 36px;
  --faq-content-width: 1120px;
  --faq-side-padding: 400px;
  --faq-padding-top: 59px;
  --faq-title-font: 50px;
  --faq-title-line: 70px;
  --faq-subtitle-top: 30px;
  --faq-subtitle-font: 50px;
  --faq-subtitle-line: 70px;
  --faq-description-width: 760px;
  --faq-description-top: 30px;
  --faq-description-font: 24px;
  --faq-description-line: 34px;
  --faq-list-top: 109px;
  --faq-border-width: 1px;
  --faq-item-bottom: 30px;
  --faq-question-width: 1120px;
  --faq-row-bottom: 30px;
  --faq-question-font: 20px;
  --faq-question-line: 20px;
  --faq-icon-space: 48px;
  --faq-icon-font: 20px;
  --faq-answer-gap: 20px;
  --faq-answer-padding: 30px;
  --faq-answer-radius: 20px;
  --faq-answer-font: 20px;
  --faq-answer-line: 30px;

  width: 100%;
  min-height: 100vh;
  overflow-x: hidden;
  background: #000;
}

.stage-shell {
  position: relative;
  width: 100%;
  min-height: 0;
  overflow: visible;
  background: #000;
}

.stage {
  position: relative;
  width: var(--stage-width);
  height: var(--stage-height);
  overflow: hidden;
  background: #000;
}

.stage--top {
  height: auto;
}

.support-faq-flow {
  position: relative;
  z-index: 3;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: auto;
  min-height: 0;
  background: #000;
}
</style>
